/**
 * Custom hook for managing conversation filters
 * 
 * Provides CRUD operations for filters with optimistic updates and loading states.
 */

import { useState, useEffect, useCallback } from 'react'
import {
    conversationsApi,
    type ConversationFilterResponse,
    type CreateFilterRequest
} from '@/services/conversationsApi'

export interface UseConversationFiltersReturn {
    /** List of saved filters */
    filters: ConversationFilterResponse[]
    /** Loading state for initial fetch */
    loading: boolean
    /** Error message if any */
    error: string | null
    /** Create a new filter */
    createFilter: (data: CreateFilterRequest) => Promise<ConversationFilterResponse>
    /** Update an existing filter */
    updateFilter: (filterId: string, data: Partial<CreateFilterRequest>) => Promise<ConversationFilterResponse>
    /** Delete a filter (soft delete) */
    deleteFilter: (filterId: string) => Promise<void>
    /** Refetch all filters */
    refetch: () => Promise<void>
    /** Creating state */
    isCreating: boolean
    /** Deleting state (filter ID being deleted) */
    deletingId: string | null
}

export function useConversationFilters(): UseConversationFiltersReturn {
    const [filters, setFilters] = useState<ConversationFilterResponse[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [deletingId, setDeletingId] = useState<string | null>(null)

    const normalizeFiltersPayload = (payload: unknown): ConversationFilterResponse[] => {
        if (Array.isArray(payload)) {
            return payload as ConversationFilterResponse[]
        }

        if (payload && typeof payload === 'object') {
            const maybeResults = (payload as { results?: unknown }).results
            if (Array.isArray(maybeResults)) {
                return maybeResults as ConversationFilterResponse[]
            }

            const maybeData = (payload as { data?: unknown }).data
            if (Array.isArray(maybeData)) {
                return maybeData as ConversationFilterResponse[]
            }
        }

        return []
    }

    /**
     * Fetch all filters from the API
     */
    const fetchFilters = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await conversationsApi.getFilters()
            setFilters(normalizeFiltersPayload(data))
        } catch (err) {
            console.error('Failed to load filters:', err)
            setError('Failed to load filters')
            setFilters([])
        } finally {
            setLoading(false)
        }
    }, [])

    /**
     * Create a new filter
     */
    const createFilter = useCallback(async (data: CreateFilterRequest): Promise<ConversationFilterResponse> => {
        try {
            setIsCreating(true)
            setError(null)
            const newFilter = await conversationsApi.createFilter(data)
            // Optimistic update: add to the beginning of the list
            setFilters(prev => [newFilter, ...prev])
            return newFilter
        } catch (err) {
            console.error('Failed to create filter:', err)
            setError('Failed to create filter')
            throw err
        } finally {
            setIsCreating(false)
        }
    }, [])

    /**
     * Update an existing filter
     */
    const updateFilter = useCallback(async (
        filterId: string,
        data: Partial<CreateFilterRequest>
    ): Promise<ConversationFilterResponse> => {
        try {
            setError(null)
            const updatedFilter = await conversationsApi.updateFilter(filterId, data)
            // Optimistic update: replace in the list
            setFilters(prev => prev.map(f => f.id === filterId ? updatedFilter : f))
            return updatedFilter
        } catch (err) {
            console.error('Failed to update filter:', err)
            setError('Failed to update filter')
            throw err
        }
    }, [])

    /**
     * Delete a filter (soft delete)
     */
    const deleteFilter = useCallback(async (filterId: string): Promise<void> => {
        try {
            setDeletingId(filterId)
            setError(null)
            await conversationsApi.deleteFilter(filterId)
            // Optimistic update: remove from list
            setFilters(prev => prev.filter(f => f.id !== filterId))
        } catch (err) {
            console.error('Failed to delete filter:', err)
            setError('Failed to delete filter')
            throw err
        } finally {
            setDeletingId(null)
        }
    }, [])

    // Initial fetch on mount
    useEffect(() => {
        fetchFilters()
    }, [fetchFilters])

    return {
        filters,
        loading,
        error,
        createFilter,
        updateFilter,
        deleteFilter,
        refetch: fetchFilters,
        isCreating,
        deletingId,
    }
}

export type { ConversationFilterResponse, CreateFilterRequest }
